import api from '@/utils/api';
import timesheetArrivalLabel from '@/utils/timesheetArrivalLabel';

export default {
    data: () => ({hoursArrivalRequirement: null, hoursArrivalPosition: 0, hoursArrivalTotal: 0, hoursArrivalOperation: 'entries', unresolvedResolutionPending: false,
        arrivalDiscrepancyReason: '', arrivalDiscrepancyAccepted: false, standaloneArrivalRequirements: [], hoursArrivalBlocked: false}),
    created() { this._arrivalDrafts = new Map(); },
    beforeDestroy() { this.cancelHoursArrival(); },
    watch: {
        canEdit(value) { if (!value && this.hoursArrivalOperation !== 'resolution') this.closeUnavailableArrival(); },
        canResolveUnresolved(value) { if (!value && this.hoursArrivalOperation === 'resolution') this.closeUnavailableArrival(); }
    },
    computed: {
        hoursArrivalAffected() {
            const requirement = this.hoursArrivalRequirement;
            if (!requirement) return this.standaloneArrivalRequirements.filter(item => item.arrival_date === this.plannedArrivalEffectiveDate)
                .flatMap(item => item.affected_entries || []);
            if (this.plannedArrivalEffectiveDate === requirement.arrival_date) return requirement.affected_entries || [];
            // Changing the date invalidates the server's warning. The next submit
            // obtains a fresh list, including baseline and previous-visit exclusions.
            return [];
        }
    },
    methods: {
        timesheetArrivalLabel,
        closeUnavailableArrival() {
            this.cancelHoursArrival(); this.plannedArrivalModalVisible = false;
        },
        rememberHoursArrival() {
            const requirement = this.hoursArrivalRequirement;
            if (requirement) this._arrivalDrafts.set(requirement.key, {arrival_date: this.plannedArrivalEffectiveDate,
                reason: this.arrivalDiscrepancyReason, accept_discrepancy: this.arrivalDiscrepancyAccepted,
                warning_token: requirement.warning_token});
        },
        cancelHoursArrival() {
            this.rememberHoursArrival();
            if (this._resolveHoursArrival) {
                this._resolveHoursArrival(null);
                this._resolveHoursArrival = null;
            }
        },
        requestHoursArrival(requirement, position, total) {
            if (this.plannedArrivalModalVisible && this.plannedArrivalModalItem && !this.hoursArrivalRequirement) {
                this._arrivalDrafts.set(`planned:${this.plannedArrivalModalItem.key}`, {
                    arrival_date: this.plannedArrivalEffectiveDate, reason: this.arrivalDiscrepancyReason
                });
            }
            const draft = this._arrivalDrafts.get(requirement.key) || {};
            this.hoursArrivalRequirement = requirement;
            this.hoursArrivalBlocked = false;
            this.hoursArrivalPosition = position; this.hoursArrivalTotal = total;
            this.plannedMovePendingId = null;
            this.plannedArrivalModalItem = {...requirement, target_type: 'object'};
            this.plannedArrivalEffectiveDate = requirement.existing_confirmation ? requirement.arrival_date : draft.arrival_date || requirement.arrival_date;
            this.arrivalDiscrepancyReason = draft.reason || '';
            this.arrivalDiscrepancyAccepted = draft.warning_token === requirement.warning_token && Boolean(draft.accept_discrepancy);
            this.plannedArrivalConfirmationWarning = requirement.assignment_warning || null;
            this.plannedArrivalModalError = '';
            this.plannedArrivalReserveError = false;
            this.plannedArrivalModalVisible = true;
            return new Promise(resolve => {this._resolveHoursArrival = resolve;});
        },
        acceptHoursArrival() {
            if (this.hoursArrivalAffected.length && (!this.arrivalDiscrepancyReason.trim() || !this.arrivalDiscrepancyAccepted)) {
                this.plannedArrivalModalError = 'Объясните расхождение и явно подтвердите его.';
                return;
            }
            this.rememberHoursArrival();
            const resolve = this._resolveHoursArrival;
            this._resolveHoursArrival = null;
            if (resolve) resolve({...this.hoursArrivalRequirement,
                arrival_date: this.plannedArrivalEffectiveDate, reason: this.arrivalDiscrepancyReason.trim(),
                accept_discrepancy: this.arrivalDiscrepancyAccepted,
                confirm_plan_mismatch: Boolean(this.plannedArrivalConfirmationWarning),
                confirm_fact_replacement: Boolean(this.plannedArrivalConfirmationWarning)});
        },
        putDayEntriesWithArrivals(sheetId, entries, config) {
            return this.withArrivalConfirmations(choices => api.put(`/api/timesheets/${sheetId}/day-entries`, {
                entries, arrival_confirmations: choices
            }, {...config, ignoreModal: true}), 'entries');
        },
        async resolveUnresolvedWithArrivals(sheetId, lineId, payload) {
            this.unresolvedResolutionPending = true;
            try {
                if (!await this.flushTimesheetEdits()) throw Error('Сначала сохраните изменения в табеле и устраните ошибки. Выбор карточки сохранён.');
                return await this.withArrivalConfirmations(choices => api.post(
                    `/api/timesheets/${sheetId}/lines/${lineId}/resolve-unresolved`,
                    {...payload, arrival_confirmations: choices}, {ignoreModal: true}
                ), 'resolution');
            } finally { this.unresolvedResolutionPending = false; }
        },
        async withArrivalConfirmations(send, operation) {
            const choices = new Map();
            let ownsModal = false;
            try {
                for (;;) {
                    try {
                        const response = await send([...choices.values()]);
                        choices.forEach(choice => this._arrivalDrafts.delete(choice.key));
                        if (ownsModal) this.plannedArrivalModalVisible = false;
                        return response;
                    } catch (error) {
                        const body = error && error.response && error.response.data;
                        if (!body || body.code !== 'TIMESHEET_ARRIVAL_CONFIRMATION_REQUIRED') {
                            if (!ownsModal || !this.hoursArrivalRequirement || !this.plannedArrivalModalVisible) throw error;
                            if ([401, 403, 409].includes(Number(error.response && error.response.status))) {
                                this.hoursArrivalBlocked = true;
                                throw error;
                            }
                            const retry = this.requestHoursArrival(this.hoursArrivalRequirement, this.hoursArrivalPosition, this.hoursArrivalTotal);
                            this.plannedArrivalModalError = this.responseMessage(error, 'Не удалось сохранить. Ввод не записан и будет потерян при обновлении страницы.');
                            const choice = await retry;
                            if (!choice) throw error;
                            choices.set(choice.key, choice);
                            this.plannedMovePendingId = 'hours';
                            continue;
                        }
                        const requirements = body.details && body.details.requirements || [];
                        if (!requirements.length) throw error;
                        ownsModal = true;
                        this.hoursArrivalOperation = operation;
                        for (let i = 0; i < requirements.length; i += 1) {
                            const choice = await this.requestHoursArrival(requirements[i], i + 1, requirements.length);
                            if (!choice) throw Object.assign(new Error(operation === 'resolution'
                                ? 'Связывание отменено. Ручная строка и сохранённые отметки остались без изменений.'
                                : 'Часы не внесены: прибытие не подтверждено'),
                                {code: 'TIMESHEET_ARRIVAL_CONFIRMATION_CANCELLED'});
                            choices.set(choice.key, choice);
                        }
                        this.plannedArrivalModalError = '';
                        this.plannedMovePendingId = 'hours';
                    }
                }
            } catch (error) {
                this.plannedArrivalReserveError = Boolean(error && error.response && error.response.data && error.response.data.code === 'RESERVE_EMPLOYEE_CANNOT_BE_ASSIGNED');
                if (ownsModal && this.plannedArrivalModalVisible) this.plannedArrivalModalError = this.responseMessage(error,
                    operation === 'resolution' ? 'Не удалось связать строку. Сохранённые отметки не потеряны.' : 'Отметки не записаны. При обновлении страницы несохранённый ввод будет потерян.');
                throw error;
            } finally {
                if (ownsModal) {
                    this.plannedMovePendingId = null;
                    this._resolveHoursArrival = null;
                }
            }
        }
    }
};
